import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {HubspotService} from './hubspot.service';
import {ClientService} from '../auth/client.service';
import {EnrichSettings, HubspotReadinessDimension, HubspotReadinessResponse, HubspotSyncHealth} from '../../models/hubspot.model';
import {
  ConnectConnectionPermissions,
  ConnectConnectionsService,
  ConnectServerConnection
} from '../connect/connect-connections.service';

export type EnrichReadinessDimensionKey =
  | 'connection'
  | 'scopes'
  | 'settings'
  | 'properties'
  | 'webhooks'
  | 'mapping'
  | 'syncHealth';

export type EnrichReadinessStatus = 'ready' | 'actionRequired' | 'unknown';

export interface EnrichReadinessDimension {
  key: EnrichReadinessDimensionKey;
  label: string;
  status: EnrichReadinessStatus;
  requiredForOperations: boolean;
  route: string;
  message: string;
  messageCode?: string;
  evidence?: Record<string, unknown>;
}

export interface EnrichReadiness {
  dimensions: EnrichReadinessDimension[];
  currentStep: number;
  isOperationalReady: boolean;
  localSearchOnly?: boolean;
  blockingKeys: EnrichReadinessDimensionKey[];
  runtimeUnavailable?: boolean;
  syncHealth?: HubspotSyncHealth | null;
}

export interface EnrichReadinessContext {
  selectedConnectionId?: string | null;
  selectedConnection?: ConnectServerConnection | null;
  connectionPermissions?: ConnectConnectionPermissions | null;
  connectionPermissionError?: string | null;
  authStatus?: { authenticated: boolean; error?: string } | null;
  backendReadiness?: HubspotReadinessResponse | null;
  runtimeUnavailable?: boolean;
}

export function isEnrichRuntimeUnavailableError(error: unknown): boolean {
  const status = Number((error as {status?: unknown} | null)?.status);
  return status === 0 || (status >= 500 && status < 600);
}

export function isLocalEnrichSearchRoute(url: string): boolean {
  const path = url.split(/[?#]/)[0].replace(/\/$/, '');
  return path === '/enrich/companies' || /^\/enrich\/companies\/[0-9]{9}$/.test(path) || /^\/enrich\/filters(?:\/[^/]+)?$/.test(path);
}

@Injectable({
  providedIn: 'root'
})
export class EnrichSetupService {
    private hubspotService = inject(HubspotService);
    private clientService = inject(ClientService);
    private connectConnectionsService = inject(ConnectConnectionsService);

  private currentStepSubject = new BehaviorSubject<number>(1);
  public currentStep$: Observable<number> = this.currentStepSubject.asObservable();
  private readinessSubject = new BehaviorSubject<EnrichReadiness>(this.buildReadiness(null));
  public readiness$: Observable<EnrichReadiness> = this.readinessSubject.asObservable();

    constructor() {
    // Initialize by checking the current setup status
    this.checkSetupStatus();
  }

  getCurrentReadiness(): EnrichReadiness {
    return this.readinessSubject.getValue();
  }

  updateReadinessFromSettings(
    settings: EnrichSettings | null | undefined,
    context: EnrichReadinessContext = {},
  ): EnrichReadiness {
    if (context.runtimeUnavailable) {
      const dimensions = this.unavailableDimensions();
      const readiness: EnrichReadiness = {
        dimensions,
        currentStep: 1,
        isOperationalReady: false,
        blockingKeys: dimensions.filter(dimension => dimension.requiredForOperations).map(dimension => dimension.key),
        runtimeUnavailable: true,
        syncHealth: null,
      };
      this.readinessSubject.next(readiness);
      this.currentStepSubject.next(readiness.currentStep);
      return readiness;
    }
    const readiness = this.buildReadiness(settings, context);
    this.readinessSubject.next(readiness);
    this.currentStepSubject.next(readiness.currentStep);
    return readiness;
  }

  buildReadiness(
    settings: EnrichSettings | null | undefined,
    context: EnrichReadinessContext = {},
  ): EnrichReadiness {
    const setupStatus = settings?.setupStatus;
    const selectedConnectionId =
      this.normalizeId(context.selectedConnectionId) || this.normalizeId(settings?.hubspotConnectionId);
    const setupComplete =
      setupStatus?.setupComplete === true &&
      setupStatus?.authenticated === true &&
      setupStatus?.settingsCreated === true &&
      setupStatus?.propertiesCreated === true &&
      setupStatus?.webhooksSetup === true &&
      setupStatus?.mappingSetup === true;
    const authReady = context.authStatus?.authenticated ?? setupStatus?.authenticated === true;
    const connectionConnected =
      context.connectionPermissions?.connected ??
      context.selectedConnection?.connected ??
      context.selectedConnection?.oauth_connected;
    const hasSelectedConnection = selectedConnectionId.length > 0;
    const legacyCompletedConnection = setupComplete && !hasSelectedConnection && setupStatus?.authenticated === true;

    const connectionReady =
      (hasSelectedConnection && authReady && connectionConnected !== false) || legacyCompletedConnection;
    const connectionStatus = connectionReady ? 'ready' : 'actionRequired';

    const missingScopes = (context.connectionPermissions?.missingScopes || [])
      .map(scope => String(scope || '').trim())
      .filter(Boolean);
    const permissionsKnown = context.connectionPermissions !== null && context.connectionPermissions !== undefined;
    const scopesReady =
      legacyCompletedConnection ||
      (hasSelectedConnection &&
        permissionsKnown &&
        !context.connectionPermissionError &&
        context.connectionPermissions?.hasRequiredScopes === true);
    const scopesStatus: EnrichReadinessStatus = scopesReady
      ? 'ready'
      : hasSelectedConnection && !permissionsKnown && !context.connectionPermissionError
        ? 'unknown'
        : 'actionRequired';
    const backendReadiness = context.backendReadiness ?? null;
    const backendSyncHealthDimension = this.findBackendSyncHealthDimension(backendReadiness);

    const dimensions: EnrichReadinessDimension[] = [
      {
        key: 'connection',
        label: 'Connection',
        status: connectionStatus,
        requiredForOperations: true,
        route: '/enrich/setup',
        message: connectionReady
          ? legacyCompletedConnection
            ? 'Legacy HubSpot authentication is active.'
            : 'A HubSpot connection is selected and authenticated.'
          : context.authStatus?.error || 'Select and authenticate a HubSpot connection.',
      },
      {
        key: 'scopes',
        label: 'Scopes',
        status: scopesStatus,
        requiredForOperations: true,
        route: '/enrich/setup',
        message: scopesReady
          ? legacyCompletedConnection
            ? 'Legacy setup is complete; scope verification is not available for this connection.'
            : 'Required Shine Enrich scopes are present.'
          : context.connectionPermissionError ||
            (!permissionsKnown && hasSelectedConnection
              ? 'HubSpot connection scopes have not been verified yet.'
              :
            (missingScopes.length
              ? `Missing required scopes: ${missingScopes.join(', ')}.`
              : 'Verify the selected HubSpot connection scopes.')),
      },
      {
        key: 'settings',
        label: 'Settings',
        status: setupStatus?.settingsCreated === true ? 'ready' : 'actionRequired',
        requiredForOperations: true,
        route: '/enrich/setup',
        message:
          setupStatus?.settingsCreated === true
            ? 'Required Enrich settings are saved.'
            : 'Save the required Enrich settings.',
      },
      {
        key: 'webhooks',
        label: 'Webhooks',
        status: setupStatus?.webhooksSetup === true ? 'ready' : 'actionRequired',
        requiredForOperations: true,
        route: '/enrich/setup',
        message:
          setupStatus?.webhooksSetup === true
            ? 'HubSpot webhook setup is marked as ready.'
            : 'Set up or repair HubSpot webhooks.',
      },
      {
        key: 'properties',
        label: 'Properties',
        status: setupStatus?.propertiesCreated === true ? 'ready' : 'actionRequired',
        requiredForOperations: true,
        route: '/enrich/setup',
        message:
          setupStatus?.propertiesCreated === true
            ? 'HubSpot properties are marked as created.'
            : 'Create or repair the required HubSpot properties.',
      },
      {
        key: 'mapping',
        label: 'Mapping',
        status: setupStatus?.mappingSetup === true ? 'ready' : 'actionRequired',
        requiredForOperations: true,
        route: '/enrich/mapping',
        message:
          setupStatus?.mappingSetup === true
            ? 'Field mapping is ready.'
            : 'Complete or intentionally skip field mapping.',
      },
      {
        key: 'syncHealth',
        label: backendSyncHealthDimension?.label || 'Sync health',
        status: backendSyncHealthDimension?.status || 'unknown',
        requiredForOperations: false,
        route: backendSyncHealthDimension?.route || '/enrich/sync-history',
        message: backendSyncHealthDimension?.message || 'No backend sync-health signal is available in this setup check.',
        messageCode: backendSyncHealthDimension?.messageCode,
        evidence: backendSyncHealthDimension?.evidence,
      },
    ];

    const resolvedDimensions = backendReadiness
      ? this.applyBackendDimensions(dimensions, backendReadiness.dimensions, backendReadiness.blockingKeys)
      : dimensions;
    const blockingKeys = backendReadiness
      ? backendReadiness.blockingKeys
      : resolvedDimensions
        .filter(dimension => dimension.requiredForOperations && dimension.status !== 'ready')
        .map(dimension => dimension.key);

    return {
      dimensions: resolvedDimensions,
      currentStep: this.stepForBlockingKey(blockingKeys[0]),
      isOperationalReady: backendReadiness ? backendReadiness.isOperationalReady : blockingKeys.length === 0,
      localSearchOnly: backendReadiness?.localSearchOnly === true &&
        !!this.clientService.client?.id && backendReadiness.clientId === this.clientService.client.id,
      blockingKeys,
      runtimeUnavailable: false,
      syncHealth: backendReadiness?.syncHealth ?? null,
    };
  }

  /**
   * Checks the current setup status and updates the current step
   */
  checkSetupStatus(): void {
    const clientId = this.clientService.client?.id;
    if (!clientId) {
      // If no client is selected, we can't check the setup status
      return;
    }

    this.hubspotService.getSettings(clientId).pipe(
      switchMap((settings: EnrichSettings) => forkJoin({
        context: this.resolveConnectionContext(settings.hubspotConnectionId),
        backendReadiness: this.resolveBackendReadiness(clientId),
      }).pipe(
        map(({context, backendReadiness}) => ({
          settings,
          context: {
            ...context,
            backendReadiness,
          } as EnrichReadinessContext,
        })),
      )),
      catchError(error => of({
        settings: null,
        context: {runtimeUnavailable: isEnrichRuntimeUnavailableError(error)} as EnrichReadinessContext,
      })),
    ).subscribe(({settings, context}) => this.updateReadinessFromSettings(settings, context));
  }

  private normalizeId(value: unknown): string {
    return String(value || '').trim();
  }

  private unavailableDimensions(): EnrichReadinessDimension[] {
    return [
      ['connection', 'Connection', '/enrich/setup'],
      ['scopes', 'Scopes', '/enrich/setup'],
      ['settings', 'Settings', '/enrich/setup'],
      ['webhooks', 'Webhooks', '/enrich/setup'],
      ['properties', 'Properties', '/enrich/setup'],
      ['mapping', 'Mapping', '/enrich/mapping'],
      ['syncHealth', 'Sync health', '/enrich/sync-history'],
    ].map(([key, label, route]) => ({
      key: key as EnrichReadinessDimensionKey,
      label,
      status: 'unknown' as EnrichReadinessStatus,
      requiredForOperations: key !== 'syncHealth',
      route,
      message: 'Runtime status is unavailable.',
    }));
  }

  private resolveConnectionContext(connectionId: string | null | undefined): Observable<EnrichReadinessContext> {
    const selectedConnectionId = this.normalizeId(connectionId);
    if (!selectedConnectionId) {
      return of({selectedConnectionId: null});
    }

    return this.connectConnectionsService.getPermissions(selectedConnectionId, {service: 'shine-enrich'}).pipe(
      take(1),
      map(connectionPermissions => ({
        selectedConnectionId,
        connectionPermissions,
        authStatus: {authenticated: connectionPermissions.connected !== false},
      })),
      catchError(() => of({
        selectedConnectionId,
        connectionPermissionError: 'Unable to verify HubSpot connection permissions.',
      })),
    );
  }

  private resolveBackendReadiness(clientId: string): Observable<HubspotReadinessResponse | null> {
    return this.hubspotService.getReadiness(clientId).pipe(
      take(1),
      catchError(() => of(null)),
    );
  }

  private findBackendSyncHealthDimension(
    backendReadiness: HubspotReadinessResponse | null | undefined,
  ): HubspotReadinessDimension | null {
    return backendReadiness?.dimensions?.find(dimension => dimension.key === 'syncHealth') ?? null;
  }

  private applyBackendDimensions(
    localDimensions: EnrichReadinessDimension[],
    backendDimensions: HubspotReadinessDimension[],
    backendBlockingKeys: EnrichReadinessDimensionKey[],
  ): EnrichReadinessDimension[] {
    const backendByKey = new Map(backendDimensions.map(dimension => [dimension.key, dimension]));

    return localDimensions.map(localDimension => {
      const backendDimension = backendByKey.get(localDimension.key);
      if (backendDimension) {
        return {
          ...localDimension,
          ...backendDimension,
        };
      }

      return backendBlockingKeys.includes(localDimension.key)
        ? {
          ...localDimension,
          status: 'actionRequired',
          message: `${localDimension.label} is blocked by backend readiness.`,
        }
        : localDimension;
    });
  }

  private stepForBlockingKey(key: EnrichReadinessDimensionKey | undefined): number {
    switch (key) {
      case 'connection':
      case 'scopes':
        return 1;
      case 'settings':
        return 2;
      case 'webhooks':
        return 3;
      case 'properties':
        return 4;
      case 'mapping':
        return 5;
      default:
        return 7;
    }
  }
}
